import { createContext, type ReactNode, useContext, useRef } from "react";
import type { StravaConnectedServiceDto } from "../../../common/model/connectedServiceDto";
import type { ExportFormatType } from "../../../common/model/exportFormatType";
import type { SyncStatus } from "../../../common/model/syncStatus";
import type { UserDto } from "../../../common/model/userDto";
import { createAppTrpcClient } from "../trpcClient";
import { useAuth } from "./authContext";

interface ApiContextType {
  initializeUserAsync: (user: UserDto) => Promise<UserDto | null>;
  exportUserDataAsync: (format: ExportFormatType) => Promise<string>;
  connectStravaAsync: (stravaAuthCode: string) => Promise<StravaConnectedServiceDto>;
  startStravaSyncAsync: () => Promise<{ status: SyncStatus; abortRequested: boolean }>;
  stopStravaSyncAsync: () => Promise<{ status: SyncStatus; abortRequested: boolean }>;
}

export const ApiContext = createContext<ApiContextType>({
  initializeUserAsync: async (_user: UserDto) => null,
  exportUserDataAsync: async (_format: ExportFormatType) => "",
  connectStravaAsync: async (_stravaAuthCode: string) => {
    throw new Error("connectStravaAsync is not implemented");
  },
  startStravaSyncAsync: async () => ({ status: "NOT_STARTED", abortRequested: false }),
  stopStravaSyncAsync: async () => ({ status: "NOT_STARTED", abortRequested: false }),
});

/**
 * @typedef ApiContextProviderProps
 * @property {ReactNode} children - React components that will be rendered as children of the ApiContextProviderProps.
 */
interface ApiContextProviderProps {
  children: ReactNode;
}

interface BackendConnectedService {
  user_id: string;
  service_user_id: string | null;
  service_name: "Strava" | "Oura";
  access_token: string | null;
  refresh_token: string | null;
  token_expires_at: string | null;
}

interface BackendUserInitializeResult {
  user: {
    id: string;
    user_name: string;
    display_name: string;
    email: string;
  };
  connectedServices: BackendConnectedService[];
  isNew: boolean;
}

interface BackendSyncResult {
  status: SyncStatus;
  abortRequested: boolean;
}

function mapConnectedService(service: BackendConnectedService): StravaConnectedServiceDto {
  return {
    userId: service.user_id,
    serviceUserId: service.service_user_id,
    serviceName: service.service_name,
    accessToken: service.access_token,
    refreshToken: service.refresh_token,
    tokenExpiresAt: service.token_expires_at,
    status: {},
    serviceInfoJson: {},
    etag: null,
  };
}

function mapUserInitializeResult(result: BackendUserInitializeResult): UserDto {
  return {
    userId: result.user.id,
    userName: result.user.user_name,
    displayName: result.user.display_name,
    email: result.user.email,
    connectedServices: result.connectedServices.map(mapConnectedService),
  };
}

export const ApiProvider: React.FC<ApiContextProviderProps> = ({ children }) => {
  const { getAccessTokenAsync } = useAuth();
  const isApiCallInProgress = useRef(false);
  const clientRef = useRef<ReturnType<typeof createAppTrpcClient> | null>(null);
  if (clientRef.current === null) {
    clientRef.current = createAppTrpcClient(getAccessTokenAsync);
  }
  const client = clientRef.current;

  const enqueueApiCall = async (apiFunc: () => Promise<any>): Promise<any> => {
    if (isApiCallInProgress.current) {
      throw new Error("An API call is already in progress. Please wait.");
    }

    isApiCallInProgress.current = true;
    try {
      const result = await apiFunc();
      return result;
    } finally {
      isApiCallInProgress.current = false;
    }
  };

  const initializeUserAsync = (user: UserDto): Promise<UserDto | null> => {
    return enqueueApiCall(async () => {
      void user;
      const result = await client.user.initialize.mutate();
      return mapUserInitializeResult(result as BackendUserInitializeResult);
    });
  };

  const exportUserDataAsync = (format: ExportFormatType): Promise<string> => {
    return enqueueApiCall(async () => {
      const shellExport = {
        format,
        status: "pending-server-integration",
        exportedAt: new Date().toISOString(),
      };
      return format === "json"
        ? JSON.stringify(shellExport, null, 2)
        : `status,exportedAt\npending-server-integration,${shellExport.exportedAt}`;
    });
  };

  const connectStravaAsync = (stravaAuthCode: string): Promise<StravaConnectedServiceDto> => {
    return enqueueApiCall(async () => {
      const result = await client.strava.connect.mutate({ code: stravaAuthCode });
      return mapConnectedService(result as BackendConnectedService);
    });
  };

  const startStravaSyncAsync = (): Promise<{ status: SyncStatus; abortRequested: boolean }> => {
    return enqueueApiCall(async () => {
      const result = await client.strava.sync.start.mutate();
      return {
        status: (result as BackendSyncResult).status,
        abortRequested: (result as BackendSyncResult).abortRequested,
      };
    });
  };

  const stopStravaSyncAsync = (): Promise<{ status: SyncStatus; abortRequested: boolean }> => {
    return enqueueApiCall(async () => {
      const result = await client.strava.sync.stop.mutate();
      return {
        status: (result as BackendSyncResult).status,
        abortRequested: (result as BackendSyncResult).abortRequested,
      };
    });
  };

  return (
    <ApiContext.Provider
      value={{
        initializeUserAsync,
        exportUserDataAsync,
        connectStravaAsync,
        startStravaSyncAsync,
        stopStravaSyncAsync,
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};

export const useApi = (): ApiContextType => {
  const context = useContext(ApiContext);
  if (context === undefined) {
    throw new Error("useApi must be used within a ApiProvider");
  }
  return context;
};
